import type { ApiError } from "@gymplan/shared";

const BASE = import.meta.env.VITE_API_URL ?? "";

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: "include",
    headers: body !== undefined ? { "content-type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as T | ApiError;
  if (!res.ok) {
    const e = data as ApiError;
    throw new ApiRequestError(res.status, e.error ?? res.statusText, e.details);
  }
  return data as T;
}

export const api = {
  get: <T>(p: string) => request<T>("GET", p),
  post: <T>(p: string, b?: unknown) => request<T>("POST", p, b),
  put: <T>(p: string, b?: unknown) => request<T>("PUT", p, b),
  patch: <T>(p: string, b?: unknown) => request<T>("PATCH", p, b),
  del: <T>(p: string, b?: unknown) => request<T>("DELETE", p, b),
};

// ---- Shared response types (kept loose; the API is the source of truth) ----
export interface Gym {
  id: string;
  name: string;
  city?: string | null;
  postcode?: string | null;
  verified?: boolean;
}

export interface AvailabilitySlot {
  id?: string;
  weekday: string;
  startTime: string;
  endTime: string;
  gymId: string | null;
}

export interface ClientPreference {
  id?: string;
  weekday: string;
  startTime: string | null;
  endTime: string | null;
  gyms: { gym: { id: string; name: string } }[];
}

export interface Client {
  id: string;
  name: string;
  phone: string;
  email?: string | null;
  sessionsPerWeek: number;
  /** ISO date-time at midnight UTC, e.g. "1992-03-14T00:00:00.000Z" */
  dateOfBirth?: string | null;
  gender?: string | null;
  genderDescription?: string | null;
  active: boolean;
  whatsappOptIn?: boolean;
  /** Null = imported by the trainer and invited, but hasn't given their own details yet */
  joinedAt?: string | null;
  invitedAt?: string | null;
  inviteRemindedAt?: string | null;
  /** False = "just book my usual times" (asked only when the trainer's hours change that week) */
  askEachWeek?: boolean;
  preferences: ClientPreference[];
}

export interface ClientSummary {
  total: number;
  joined: number;
  notJoined: { id: string; name: string; invitedAt: string | null; remindedAt: string | null }[];
  canRemind: number;
  clientsToldAt: string | null;
  everPlanned: boolean;
  rackupNumber: string | null;
}

export interface CheckIn {
  id: string;
  status: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "EXPIRED";
  result: { keepPreferences: boolean; overrides: unknown[]; notes?: string } | null;
  sentAt: string | null;
  completedAt: string | null;
  offeredAt?: string | null;
  client: { id: string; name: string; phone: string; sessionsPerWeek: number };
}
